import {
    isRuleVisibleByMatchingCount,
    ruleMatchesCategory,
} from "@common/helpers/rules";
import { parseSimilarToValue } from "@common/helpers/split-aware-description.helper";
import { TransactionRuleReviewWithReviewer } from "@common/schemas/transactionRuleReview.schema";
import { Class } from "@common/types/class";
import { Counterparty } from "@common/types/counterparty";
import { Account } from "@common/types/domains/accounting/account";
import { Entity } from "@common/types/entity";
import { FiltersTypes } from "@common/types/filters/general";
import { FinancialAccount } from "@common/types/financialAccount";
import { NormalizedDescriptionInfo } from "@common/types/normalizedDescription";
import {
    RuleActionType,
    RuleConditionAmount,
    RuleConditionSimilarTo,
    RuleConditionType,
    RuleGroupType,
    RuleSource,
    RuleSourceFilter,
    RuleStatus,
    TransactionRule,
    TransactionRuleCondition,
    TransactionRuleGroup,
    TransferRuleDirection,
    parseAccountIdActionValue,
    parseAccrualAccountActionValue,
} from "@common/types/transactionRule";
import { defaultRuleSources } from "@components/pageHeader/filters/lib";
import {
    RuleReviewFilters,
    ruleMatchesReviewFilters,
} from "./ruleReviewFilters";
import { RuleValueFilters, ruleMatchesValueFilters } from "./ruleValueFilters";

// AI-rule visibility is driven by the live `matchingCounts` (not the
// applications-based `transactionCounts`, which drifts stale-low — PRO-9305).
// Single-map signature removes the two-same-typed-args swap footgun.
export function isRuleVisible(
    rule: Pick<TransactionRule, "id" | "source">,
    matchingCounts: Record<string, number>,
): boolean {
    return isRuleVisibleByMatchingCount(rule, matchingCounts);
}

export function isRuleVisibleForFeatures(
    rule: Pick<TransactionRule, "actions">,
    options: { isSchedulesEnabled: boolean },
): boolean {
    if (options.isSchedulesEnabled) {
        return true;
    }
    const hasScheduleAction = rule.actions.some(
        (action) => action.actionType === RuleActionType.ASSIGN_TO_SCHEDULE,
    );
    const hasOtherAction = rule.actions.some(
        (action) => action.actionType !== RuleActionType.ASSIGN_TO_SCHEDULE,
    );
    return !hasScheduleAction || hasOtherAction;
}

export function resolveRuleSources(
    ruleSources: RuleSourceFilter[] | undefined,
): RuleSourceFilter[] {
    return ruleSources?.length ? ruleSources : defaultRuleSources;
}

// Kick similarity rules matching 0-1 transactions are pure noise on the Rules
// page, so they are hidden unconditionally — no source filter reveals them.
// Rules-page-only threshold; the shared `isRuleVisibleByMatchingCount` (>0)
// also gates similarity-cluster exposure elsewhere (PRO-11195).
const MIN_VISIBLE_KICK_RULE_MATCHES = 2;

export function isRuleVisibleBySource({
    rule,
    matchingCounts,
    ruleSources,
}: {
    rule: Pick<TransactionRule, "id" | "source">;
    matchingCounts: Record<string, number>;
    ruleSources: RuleSourceFilter[] | undefined;
}): boolean {
    const sources = resolveRuleSources(ruleSources);

    if (rule.source !== RuleSource.NORMALIZATION) {
        return sources.includes(RuleSourceFilter.CUSTOM);
    }

    if ((matchingCounts[rule.id] ?? 0) < MIN_VISIBLE_KICK_RULE_MATCHES) {
        return false;
    }

    return sources.includes(RuleSourceFilter.MATCHED_KICK);
}

interface BuilRulesItemsProps {
    search: string | undefined;
    categoryLabelGetter: (id: string) => string;
    ruleGroups?: TransactionRuleGroup[];
    entities: Entity[];
    financialAccounts: FinancialAccount[];
    accounts: Array<Account & { entityId: number }>;
    counterparties: Counterparty[];
    normalizedDescriptions: NormalizedDescriptionInfo[];
    ruleReviews: Record<string, TransactionRuleReviewWithReviewer[]>;
    classesMap: Map<string, Class>;
    filters: Pick<
        FiltersTypes,
        "entityIds" | "ruleStatuses" | "category" | "ruleActionTypes"
    > &
        RuleValueFilters &
        RuleReviewFilters;
    activeTab: RuleGroupType;
}

export function getRuleTitle(type: RuleGroupType) {
    if (type === RuleGroupType.ACCOUNTING) {
        return "Accounting rules";
    } else if (type === RuleGroupType.TRANSFER) {
        return "Transfer rules";
    } else if (type === RuleGroupType.TRANSACTION) {
        return "Transactions rules";
    } else if (type === RuleGroupType.SPLIT) {
        return "Split rules";
    }
    return "Rules";
}

export const isCategoryFilterApplicableTab = (tab: RuleGroupType): boolean =>
    tab === RuleGroupType.TRANSACTION || tab === RuleGroupType.ACCOUNTING;

export const isCounterpartyOrClassFilterApplicableTab = (
    tab: RuleGroupType,
): boolean => tab === RuleGroupType.TRANSACTION || tab === RuleGroupType.SPLIT;

export const getFieldNamePrefix = (
    index: number,
    transferDirection?: TransferRuleDirection,
) =>
    transferDirection
        ? `conditions.${transferDirection}.${index}`
        : `conditions.${index}`;

// The Actions filter offers only canonical types (e.g. "Assign to cash
// ledger"), so the by-name action variants collapse onto their by-id types.
const canonicalRuleActionType = (
    actionType: RuleActionType,
): RuleActionType => {
    switch (actionType) {
        case RuleActionType.ACCOUNT_NAME:
            return RuleActionType.ACCOUNT_ID;
        case RuleActionType.ACCRUAL_ACCOUNT_NAME:
            return RuleActionType.ACCRUAL_ACCOUNT_ID;
        default:
            return actionType;
    }
};

export function ruleMatchesActionTypes(
    rule: Pick<TransactionRule, "actions">,
    ruleActionTypes: RuleActionType[] | undefined,
): boolean {
    if (!ruleActionTypes || ruleActionTypes.length === 0) {
        return true;
    }

    const selectedActionTypes = new Set(
        ruleActionTypes.map(canonicalRuleActionType),
    );

    return rule.actions.some((action) =>
        selectedActionTypes.has(canonicalRuleActionType(action.actionType)),
    );
}

export function buildRulesItems({
    search,
    categoryLabelGetter,
    ruleGroups,
    entities,
    financialAccounts,
    counterparties,
    normalizedDescriptions,
    ruleReviews,
    filters,
    accounts,
    classesMap,
    activeTab,
}: BuilRulesItemsProps) {
    if (!ruleGroups || (!search && Object.keys(filters).length === 0)) {
        return ruleGroups;
    }

    const counterpartiesMap = new Map(counterparties.map((c) => [c.id, c]));
    const normalizedDescriptionsMap = new Map(
        normalizedDescriptions.map((d) => [d.id, d.normalizedDescription]),
    );
    const entitiesMap = new Map(entities.map((e) => [e.id, e]));
    const financialAccountsMap = new Map(
        financialAccounts.map((fa) => [fa.id, fa]),
    );
    const accountsMap = new Map(accounts.map((a) => [a.id, a]));
    const categoryIdSet = new Set(filters.category ?? []);

    const filteredRuleGroups = ruleGroups
        .filter(({ type }) => type === activeTab)
        .map((ruleGroup) => {
            const filteredRules = (ruleGroup.rules ?? []).filter((rule) => {
                const conditionMatch = search
                    ? searchConditions({
                          conditions: rule.conditions,
                          search,
                          entitiesMap,
                          financialAccountsMap,
                          counterpartiesMap,
                          normalizedDescriptionsMap,
                          categoryLabelGetter,
                          classesMap,
                      })
                    : true;

                const conditionsFilter = filterConditions(
                    rule.conditions,
                    filters,
                );

                const actionMatch = search
                    ? searchActions({
                          rule,
                          search,
                          accountsMap,
                          entitiesMap,
                          counterpartiesMap,
                          categoryLabelGetter,
                          classesMap,
                      })
                    : true;

                const statusMatch =
                    !filters.ruleStatuses ||
                    filters.ruleStatuses.length === 0 ||
                    (filters.ruleStatuses.includes(RuleStatus.ENABLED) &&
                        !rule.isDisabled) ||
                    (filters.ruleStatuses.includes(RuleStatus.ARCHIVED) &&
                        !!rule.isDisabled);

                const categoryMatch =
                    categoryIdSet.size > 0
                        ? ruleMatchesCategory(rule, categoryIdSet)
                        : true;

                const actionTypeMatch = ruleMatchesActionTypes(
                    rule,
                    filters.ruleActionTypes,
                );

                const valueMatch = ruleMatchesValueFilters({
                    rule,
                    filters,
                    accounts,
                });

                const reviewMatch = ruleMatchesReviewFilters({
                    reviews: ruleReviews[rule.id] ?? [],
                    filters,
                });

                return (
                    (conditionMatch || actionMatch) &&
                    conditionsFilter &&
                    statusMatch &&
                    categoryMatch &&
                    actionTypeMatch &&
                    valueMatch &&
                    reviewMatch
                );
            });

            return {
                ...ruleGroup,
                rules: filteredRules,
            };
        })
        .filter((group) => group !== undefined);

    return filteredRuleGroups;
}

function searchActions({
    rule,
    search,
    accountsMap,
    entitiesMap,
    counterpartiesMap,
    categoryLabelGetter,
    classesMap,
}: {
    rule: TransactionRule;
    search: string;
    accountsMap: Map<string, Account>;
    entitiesMap?: Map<number, Entity>;
    counterpartiesMap?: Map<string, Counterparty>;
    categoryLabelGetter?: (id: string) => string;
    classesMap: Map<string, Class>;
}) {
    return rule.actions.some((action) => {
        switch (action.actionType) {
            case RuleActionType.ACCOUNT_ID:
                return (parseAccountIdActionValue(action.value) ?? []).some(
                    (accountId) =>
                        containsTextCaseInsensitive(
                            accountsMap.get(accountId)?.name,
                            search,
                        ),
                );
            case RuleActionType.ACCRUAL_ACCOUNT_ID: {
                const parsed = parseAccrualAccountActionValue(action.value);
                return (parsed.accountIds ?? []).some((accountId) =>
                    containsTextCaseInsensitive(
                        accountsMap.get(accountId)?.name,
                        search,
                    ),
                );
            }
            case RuleActionType.CLASS:
                return searchClassAction({
                    search,
                    classesMap,
                    classIds: action.value as string[],
                });
            case RuleActionType.ACCOUNT_NAME:
            case RuleActionType.MEMO:
                return containsTextCaseInsensitive(
                    action.value as string,
                    search,
                );
            case RuleActionType.ACCRUAL_ACCOUNT_NAME: {
                const parsed = parseAccrualAccountActionValue(action.value);
                return containsTextCaseInsensitive(parsed.accountName, search);
            }
            case RuleActionType.CATEGORY:
                return categoryLabelGetter
                    ? searchCategoryCondition({
                          search,
                          categoryId: action.value as string,
                          categoryLabelGetter,
                      })
                    : false;
            case RuleActionType.ENTITY:
                return entitiesMap
                    ? containsTextCaseInsensitive(
                          entitiesMap.get(action.value as number)?.name,
                          search,
                      )
                    : false;
            case RuleActionType.COUNTERPARTY:
            case RuleActionType.CUSTOMER:
                return counterpartiesMap
                    ? containsTextCaseInsensitive(
                          counterpartiesMap.get(action.value as string)?.name,
                          search,
                      )
                    : false;
            default:
                return false;
        }
    });
}

function searchClassAction({
    search,
    classesMap,
    classIds,
}: {
    search: string;
    classesMap: Map<string, Class>;
    classIds: string[];
}) {
    return classIds.some((classId) =>
        containsTextCaseInsensitive(classesMap.get(classId)?.label, search),
    );
}

const filterConditions = (
    conditions: TransactionRuleCondition[],
    filters: Pick<FiltersTypes, "entityIds">,
) => {
    const entityFilters = filters.entityIds;
    if (entityFilters?.length) {
        return conditions.every(
            (condition) =>
                condition.conditionType !== RuleConditionType.ENTITY ||
                (condition.value as number[]).some((id) =>
                    entityFilters.includes(id),
                ),
        );
    }

    return true;
};
function searchConditions({
    conditions,
    search,
    entitiesMap,
    financialAccountsMap,
    counterpartiesMap,
    normalizedDescriptionsMap,
    categoryLabelGetter,
    classesMap,
}: {
    conditions: TransactionRuleCondition[];
    search: string;
    entitiesMap: Map<number, Entity>;
    financialAccountsMap: Map<number, FinancialAccount>;
    counterpartiesMap: Map<string, Counterparty>;
    normalizedDescriptionsMap: Map<string, string>;
    categoryLabelGetter: (id: string) => string;
    classesMap: Map<string, Class>;
}) {
    return conditions.some((condition) => {
        switch (condition.conditionType) {
            case RuleConditionType.COUNTERPARTY:
                return containsTextCaseInsensitive(
                    counterpartiesMap.get(condition.value as string)?.name,
                    search,
                );
            case RuleConditionType.CATEGORY:
            case RuleConditionType.SUGGESTED_CATEGORY:
                return searchCategoryCondition({
                    search,
                    categoryId: condition.value as string,
                    categoryLabelGetter,
                });
            case RuleConditionType.DESCRIPTION_IS:
            case RuleConditionType.DESCRIPTION_INCLUDES: {
                const descriptions = Array.isArray(condition.value)
                    ? condition.value
                    : [condition.value];

                return (descriptions as string[]).some((description) =>
                    containsTextCaseInsensitive(description, search),
                );
            }
            case RuleConditionType.AMOUNT:
                return searchAmountCondition(
                    Number(search),
                    condition.value as RuleConditionAmount,
                );
            case RuleConditionType.FINANCIAL_ACCOUNTS:
                return (condition.value as number[]).some((id) =>
                    containsTextCaseInsensitive(
                        financialAccountsMap.get(id)?.name,
                        search,
                    ),
                );
            case RuleConditionType.ENTITY:
                return (condition.value as number[]).some((id) =>
                    containsTextCaseInsensitive(
                        entitiesMap.get(id)?.name,
                        search,
                    ),
                );
            case RuleConditionType.CLASS:
                return searchClassAction({
                    search,
                    classesMap,
                    classIds: condition.value as string[],
                });
            case RuleConditionType.SIMILAR_TO: {
                const { normalizedDescriptionId } = parseSimilarToValue(
                    condition.value as string | RuleConditionSimilarTo,
                );
                return containsTextCaseInsensitive(
                    normalizedDescriptionsMap.get(normalizedDescriptionId),
                    search,
                );
            }
            default:
                return false;
        }
    });
}

function searchCategoryCondition({
    search,
    categoryId,
    categoryLabelGetter,
}: {
    search: string;
    categoryId: string;
    categoryLabelGetter: (id: string) => string;
}) {
    if (!categoryId) {
        return false;
    }
    return containsTextCaseInsensitive(categoryLabelGetter(categoryId), search);
}

function searchAmountCondition(search: number, amount: RuleConditionAmount) {
    if (!amount || isNaN(search)) {
        return false;
    }
    return amount.min === search || amount.max === search;
}

function containsTextCaseInsensitive(
    text: string | undefined | null,
    search: string,
): boolean {
    if (!text) {
        return false;
    }
    return text.toLowerCase().includes(search.toLowerCase());
}
